import { useAppSettings } from '@/hooks/useAppSettings';
import { useAuth } from '@/hooks/useAuth';
import { isBuyerSession } from '@/lib/buyer-session';

/**
 * Warms the `['app-settings']` cache once a session exists (#3266 PR 7).
 * Renders nothing.
 *
 * WHY THIS EXISTS. `ListingHubRoute` routes on `app_settings.deal_hub_enabled`,
 * and `useDealHubEnabled` reports "off" while that query is still loading — it
 * must, since failing closed is what stops a failed settings read from
 * half-enabling the relocation. The cost is that "off" and "not known yet" are
 * the same value at the moment of the decision, so a COLD cache makes a
 * converted listing take two hops with a frame of the listing hub in between.
 *
 * The obvious alternative — hold first paint until the flag resolves — is
 * worse. `useAppSettings` carries no `retry: false`, so a failing read keeps
 * `isLoading` true across three backoff attempts: seconds of spinner on the
 * app's most-used page, in the state production is actually in. Warming the
 * cache removes the cold window without putting anything in front of anyone.
 *
 * IT CALLS `useAppSettings` ITSELF RATHER THAN DECLARING ITS OWN QUERY, and
 * that is the whole design. A second `useQuery` on the same key with a
 * different `staleTime` and `retry` would be two observers of one cache entry
 * disagreeing about when it is stale — the 5-minute consumer would keep
 * considering data the 30-minute warmer thinks is fresh, and query-level
 * options like `retry` resolve to whichever observer mounted last. One hook,
 * one set of options, no divergence.
 *
 * THE GATE EXCLUDES BUYERS, not merely signed-out visitors, and the difference
 * matters. A buyer IS an `auth.users` row, so `!!user` alone would have fired
 * this on every buyer session — and it would not even have been the empty read
 * an earlier version of this comment claimed: the second `app_settings` policy
 * (`20260807214003`) returns `buyer_onboarding_enabled` to any authenticated
 * user, so a buyer would have pulled and cached a settings row on their device.
 * That runs against a decision already made in the other direction —
 * `useBuyerOnboarding` deliberately reads its one key directly rather than
 * through `useAppSettings`, "to avoid caching the whole settings table on a
 * buyer's device". Staff are the only session that routes through
 * `ListingHubRoute`, so staff are the only session that needs the warm cache.
 *
 * The gate is structural rather than an `enabled` flag — an outer component
 * decides, an inner one calls the hook — which keeps the hook call
 * unconditional.
 */
export function AppSettingsWarmup() {
  const { user } = useAuth();
  if (!user || isBuyerSession(user)) return null;
  return <WarmAppSettings />;
}

function WarmAppSettings() {
  useAppSettings();
  return null;
}
